import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './side-sheet.css';

const EXIT_MS = 220;

/**
 * A floating detail sheet that slides in from the right edge, over the page.
 *
 * NON-modal on purpose: no backdrop and no focus trap, so the list underneath
 * stays clickable and a click on another row simply swaps the sheet's
 * content. Esc closes it. Bootstrap modals opened from inside the sheet stack
 * above it (z-index 1040 < 1050).
 *
 * Not react-bootstrap's Offcanvas — the bundled theme CSS predates Bootstrap
 * 5's offcanvas module, so it renders unstyled (see AssetDetail.jsx).
 *
 * The last open children are kept through the exit animation, so callers can
 * render `{selected && <Detail />}` style content without the sheet blanking
 * on its way out.
 */
// Overlays that stack above the sheet but render outside its DOM node
// (Bootstrap modals/popovers/tooltips, react-select portalled menus, toasts).
// A click inside one of these is never "outside".
const STACKED_OVERLAY_SELECTOR = '.modal, .popover, .tooltip, .dropdown-menu, .Toastify, [class*="__menu"]';

/**
 * @param {boolean} closeOnClickOutside  Light-dismiss: a pointerdown anywhere
 *   outside the sheet closes it — except inside `keepOpenWithin` (the list
 *   whose rows swap the sheet's content) and stacked overlays. Off by default
 *   so existing callers keep their behaviour.
 * @param {string} keepOpenWithin  CSS selector for elements whose clicks must
 *   not dismiss (e.g. the row buttons feeding the sheet).
 */
export default function SideSheet({ open, onClose, label, width = 560, closeOnClickOutside = false, keepOpenWithin, children }) {
    const [mounted, setMounted] = useState(open);
    const sheetRef = useRef(null);
    const lastChildren = useRef(children);
    if (open) lastChildren.current = children;

    useEffect(() => {
        if (open) {
            setMounted(true);
            return undefined;
        }
        const timer = setTimeout(() => setMounted(false), EXIT_MS);
        return () => clearTimeout(timer);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (e) => {
            if (e.key !== 'Escape' || e.defaultPrevented) return;
            // A modal on top owns Esc.
            if (document.body.classList.contains('modal-open')) return;
            onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    useEffect(() => {
        if (!open || !closeOnClickOutside) return undefined;
        const onPointerDown = (e) => {
            const target = e.target;
            if (!(target instanceof Element)) return;
            // Scrollbar drags land on <html>; scrolling the page is not a dismissal.
            if (target === document.documentElement) return;
            if (sheetRef.current && sheetRef.current.contains(target)) return;
            // A modal on top owns the click, same as Esc.
            if (document.body.classList.contains('modal-open')) return;
            if (target.closest(STACKED_OVERLAY_SELECTOR)) return;
            if (keepOpenWithin && target.closest(keepOpenWithin)) return;
            onClose();
        };
        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [open, onClose, closeOnClickOutside, keepOpenWithin]);

    if (!open && !mounted) return null;

    return ReactDOM.createPortal(
        <aside
            ref={sheetRef}
            className={`ll-side-sheet${open ? '' : ' ll-side-sheet--closing'}`}
            style={{ '--ll-side-sheet-width': `${width}px` }}
            role="dialog"
            aria-modal="false"
            aria-label={label}
        >
            {lastChildren.current}
        </aside>,
        document.body,
    );
}
